import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, skip, switchMap, throttleTime } from 'rxjs/operators';
import { RouterStateService } from '~services/router-state.service';
import { RESTRICTION_ID } from './restrictions-routing.constants';
import { environment } from '~environments/environment';
import {
  FracWithMines,
  Restriction,
  RestrictionPoint,
  RestrictionUpsert,
  RoadRestrictionAffectedSites,
  SiteMinimal,
} from './types';
import { equals, omit } from 'remeda';
import { ErrorHandlingService } from '~services/error-handling.service';
import { toLocalRestriction, RoadRestrictionApiResponse } from './toLocalRestriction';

interface RoadRestrictionAffectedSitesApiResponse {
  fracsAffected: Record<number, SiteMinimal>;
  minesAffected: Record<number, SiteMinimal>;
  fracMinePairs: { fracId: number; mineId: number; route: RestrictionPoint[] }[];
  fracMineForcedPairs: { fracId: number; mineId: number }[];
}

@Injectable({
  providedIn: 'root',
})
export class RestrictionsService {
  private fracsWithMines$$ = new BehaviorSubject<FracWithMines[]>(null);
  private fracsWithMinesShared$ = this.fracsWithMines$$.asObservable().pipe(shareReplay(1));
  private fracsWithMinesThrottle$$ = new Subject();
  public get fracsWithMines$(): Observable<FracWithMines[]> {
    this.fracsWithMinesThrottle$$.next();
    return this.fracsWithMinesShared$;
  }

  private mapLocationBounds$$ = new BehaviorSubject<{ x1: number; x2: number; y1: number; y2: number }>(null);
  private proximityFracMines$$ = new BehaviorSubject<{ fracs: SiteMinimal[]; mines: SiteMinimal[] }>({
    fracs: [],
    mines: [],
  });
  private proximityFracMinesShared$ = this.proximityFracMines$$.asObservable().pipe(shareReplay(1));
  public get proximityFracMines$(): Observable<{ fracs: SiteMinimal[]; mines: SiteMinimal[] }> {
    return this.proximityFracMinesShared$;
  }

  private restrictionList$$ = new BehaviorSubject<Record<string, Restriction>>(null);
  private restrictionListShared$ = this.restrictionList$$.asObservable().pipe(
    map((list) => {
      const response = Object.values(list || {});
      response.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
      return response;
    }),
    shareReplay(1),
  );
  private restrictionListThrottle$$ = new Subject();
  public get restrictionList$(): Observable<Restriction[]> {
    this.restrictionListThrottle$$.next();
    return this.restrictionListShared$;
  }

  private selectedRestrictionShared$ = combineLatest([
    this.routerStateService.listenForParamChange$(RESTRICTION_ID),
    this.restrictionListShared$,
  ]).pipe(
    map(([restrictionId, list]) => {
      if (!restrictionId || !list || !list.length) {
        return null;
      }
      const response = list.find((r) => r.id === +restrictionId);
      return response;
    }),
  );

  public get selectedRestriction$(): Observable<Restriction> {
    this.restrictionListThrottle$$.next();
    return this.selectedRestrictionShared$;
  }

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private routerStateService: RouterStateService,
    private errorService: ErrorHandlingService,
  ) {
    this.restrictionListThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadRestrictionList();
    });

    this.fracsWithMinesThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadFracsWithMines();
    });

    this.setupMapBoundListener();
  }

  public setMapBounds(x1: number, x2: number, y1: number, y2: number) {
    this.mapLocationBounds$$.next({
      x1,
      x2,
      y1,
      y2,
    });
  }

  public getRestrictionsInBox(x1: number, x2: number, y1: number, y2: number): Observable<Restriction[]> {
    return this.httpClient
      .get<{ restrictions: RoadRestrictionApiResponse[] }>(`${environment.api}/lmo/restrictions_in_box`, {
        params: {
          x1: `${x1}`,
          x2: `${x2}`,
          y1: `${y1}`,
          y2: `${y2}`,
        },
      })
      .pipe(map((results) => (results.restrictions || []).map(toLocalRestriction)));
  }

  public clearMapBounds() {
    this.mapLocationBounds$$.next(null);
  }

  private setupMapBoundListener() {
    this.mapLocationBounds$$
      .pipe(
        skip(1), //  no nulls
        debounceTime(250),
        distinctUntilChanged(equals),
        switchMap((bounds) => {
          if (!bounds) {
            return of(null as { fracs: SiteMinimal[]; mines: SiteMinimal[] });
          }
          return this.httpClient.get<{ fracs: SiteMinimal[]; mines: SiteMinimal[] }>(
            `${environment.api}/lmo/proximity_fracs_and_mines`,
            {
              params: {
                x1: `${bounds.x1}`,
                x2: `${bounds.x2}`,
                y1: `${bounds.y1}`,
                y2: `${bounds.y2}`,
              },
            },
          );
        }),
      )
      .subscribe(
        (results) => {
          if (results) {
            this.proximityFracMines$$.next({
              fracs: results.fracs || [],
              mines: results.mines || [],
            });
          } else {
            this.proximityFracMines$$.next({
              fracs: [],
              mines: [],
            });
          }
        },
        (error) => console.error(error),
      );
  }

  private async loadRestrictionList() {
    try {
      const response = await this.httpClient
        .get<{ restrictions: RoadRestrictionApiResponse[] }>(`${environment.api}/road_restrictions`)
        .toPromise();

      this.restrictionList$$.next(
        (response.restrictions || []).map(toLocalRestriction).reduce(
          (acc, res) => {
            acc[res.id] = res;
            return acc;
          },
          {} as Record<string, Restriction>,
        ),
      );
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadFracsWithMines() {
    try {
      const response = await this.httpClient
        .get<{ data: FracWithMines[] }>(`${environment.api}/connected_frac_mines`)
        .toPromise();
      this.fracsWithMines$$.next(response.data);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async createRestriction(body: RestrictionUpsert): Promise<boolean> {
    try {
      const response = await this.httpClient
        .post<{ restriction: RoadRestrictionApiResponse }>(`${environment.api}/road_restriction`, body)
        .toPromise();

      this.restrictionList$$.next({
        ...(this.restrictionList$$.value || {}),
        [response.restriction.id]: toLocalRestriction(response.restriction),
      });
      return true;
    } catch (error) {
      this.logger.error(error);
      this.errorService.showError('Failed to create restriction');
      return false;
    }
  }

  public async updateRestriction(id: number, body: RestrictionUpsert): Promise<boolean> {
    try {
      const response = await this.httpClient
        .put<{ restriction: RoadRestrictionApiResponse }>(`${environment.api}/road_restriction/${id}`, body)
        .toPromise();

      this.restrictionList$$.next({
        ...(this.restrictionList$$.value || {}),
        [response.restriction.id]: toLocalRestriction(response.restriction),
      });
      return true;
    } catch (error) {
      this.errorService.showError('Failed to update restriction');
      this.logger.error(error);
      return false;
    }
  }

  public async deleteRestriction(id: number): Promise<boolean> {
    try {
      await this.httpClient.delete<null>(`${environment.api}/road_restriction/${id}`).toPromise();
      this.restrictionList$$.next(omit(this.restrictionList$$.value, [`${id}`]));
      return true;
    } catch (error) {
      this.errorService.showError('Failed to remove restriction');
      this.logger.error(error);
      return false;
    }
  }

  public async toggleRestrictionActive(id: number, isActive: boolean): Promise<boolean> {
    try {
      const response = await this.httpClient
        .put<{ restriction: RoadRestrictionApiResponse }>(`${environment.api}/road_restriction/${id}/toggle_active`, {
          isActive,
        })
        .toPromise();
      this.restrictionList$$.next({
        ...(this.restrictionList$$.value || {}),
        [response.restriction.id]: toLocalRestriction(response.restriction),
      });
      return true;
    } catch (error) {
      this.errorService.showError('Failed to update restriction');
      this.logger.error(error);
      return false;
    }
  }

  public getRestrictionAffectedRoutes$(
    polygon: RestrictionPoint[],
    manualFracMinePairs: { fracId: number; mineId: number }[],
  ): Observable<RoadRestrictionAffectedSites> {
    return this.httpClient
      .post<RoadRestrictionAffectedSitesApiResponse>(`${environment.api}/road_restriction/potential_affected_routes`, {
        polygon,
        manualFracMinePairs: manualFracMinePairs.map((fm) => ({
          fracId: fm.fracId,
          mineId: fm.mineId,
          exceptionType: 'include',
        })),
      })
      .pipe(
        map((response) => {
          if (!response) {
            return {
              fracsAffected: {},
              minesAffected: {},
              fracMinePairs: [],
            };
          }

          const temp: RoadRestrictionAffectedSites = {
            fracsAffected: response.fracsAffected || {},
            minesAffected: response.minesAffected || {},
            fracMinePairs: [],
          };
          temp.fracMinePairs = (response.fracMinePairs || []).map((pair) => {
            return {
              frac: temp.fracsAffected[pair.fracId],
              mine: temp.minesAffected[pair.mineId],
              route: pair.route,
            };
          });

          return temp;
        }),
      );
  }
}
